import { useState, useEffect, useRef } from "react";
import EnhancedTable from "components/utils/Table.js";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import PatientFormService from "services/PatientFormService.js";
import FamilyService from "services/FamilyService.js";
import { useNavigate } from "react-router-dom";
import useAPI from "hooks/useAPI.js";

function RegistrationForms() {
  const [intakePacksList, setIntakePacksList] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showIntakePacks, setShowIntakePacks] = useState(false);
  const selectedPatientIdRef = useRef(null);

  const getMembersAPI = useAPI(
    FamilyService.getMembers,
    getMembersHandler,
    false,
    true
  );

  const getIntakePacksAPI = useAPI(
    PatientFormService.getIntakePacks,
    getIntakePacksHandler
  );

  useEffect(() => {
    // Load family members on component mount
    getMembersAPI.execute();
  }, []);

  function getMembersHandler(response) {
    setFamilyMembers(response.data || []);
  }

  function getIntakePacksHandler(response) {
    const transformedData = response.data.map(intakePack => transformJsonData(intakePack, selectedPatientIdRef.current));
    setIntakePacksList(transformedData);
  }

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
    selectedPatientIdRef.current = patient.id;
    setShowIntakePacks(true);
    getIntakePacksAPI.execute(patient.id);
  };

  const handleBackToPatients = () => {
    setSelectedPatient(null);
    selectedPatientIdRef.current = null;
    setShowIntakePacks(false);
    setIntakePacksList([]);
  };

  return (
    <div className="registration-forms-container">
      <h3>Patient Intakes</h3>

      {!showIntakePacks ? (
        // Show patient list
        <Paper elevation={0} className="mt-4 p-3">
          {getMembersAPI.status === "pending" ? (
            <div className="text-center">
              <CircularProgress className="text-center" sx={{ my: 20 }} />
            </div>
          ) : (
            <div>
              <Typography variant="h4" className="mb-3">
                Select a patient to view their intake packs:
              </Typography>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {familyMembers.map((member) => (
                  <Button
                    key={member.id}
                    variant="outlined"
                    onClick={() => handlePatientClick(member)}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      p: 2,
                      textTransform: 'none'
                    }}
                  >
                    <div>
                      <Typography variant="subtitle1">
                        {member.firstName} {member.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        DOB: {member.dob}
                      </Typography>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          )}
        </Paper>
      ) : (
        // Show intake packs for selected patient
        <Paper elevation={0} className="mt-4 p-3">
          <div className="mb-3">
            <Button
              variant="text"
              onClick={handleBackToPatients}
              sx={{ mb: 2 }}
            >
              ← Back to Patients
            </Button>
            <Typography variant="h4">
              Intake of {selectedPatient?.firstName} {selectedPatient?.lastName}
            </Typography>
          </div>

          {getIntakePacksAPI.status === "pending" ? (
            <div className="text-center">
              <CircularProgress className="text-center" sx={{ my: 20 }} />
            </div>
          ) : (
            <EnhancedTable
              headings={tableHeadings}
              rowsData={intakePacksList}
              rowsPerPage={10}
            />
          )}
        </Paper>
      )}

      <p className="disclaimer">
        *For Details and all files (questionnaire, program, etc.) click on VIEW
      </p>
    </div>
  );
}

const tableHeadings = [
  {
    id: "id",
    label: "ID",
  },
  {
    id: "packageName",
    label: "Intake",
  },
  {
    id: "description",
    label: "Description",
  },
  {
    id: "createdAt",
    label: "Created At",
  },
  {
    id: "view",
    label: "",
  },
];

function transformJsonData(intakePack, patientId) {
  return {
    id: intakePack.id,
    packageName: intakePack.packageName,
    description: intakePack.description,
    createdAt: intakePack.createdAt,
    view: <ViewLink patientId={patientId} intakeId={intakePack.id} />,
  };
}

function ViewLink({ patientId, intakeId }) {
  let navigate = useNavigate();
  return (
    <Link
      component="button"
      onClick={() => navigate(`/patient/${patientId}/intakes/${intakeId}`)}
      underline="none"
    >
      View
    </Link>
  );
}

export default RegistrationForms;